import React, { useState, useEffect } from "react";
import { Plus, Package, Calendar, Clock, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { useQuery } from '@tanstack/react-query';
import { api } from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import moment from "moment";
import "moment/locale/fr";
import BatchProductionModal from "./BatchProductionModal";
import Button from "../Common/Button";
import { SearchInput, ListLoader, ListEmpty, ListCard, thClass } from "../../stock/StockUI";
import { tdClass, ErrorLine, useCreateRequest } from "./technicalFilesUI";

const STATUS = {
    completed: { label: 'Complété', icon: CheckCircle, cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    in_progress: { label: 'En cours', icon: Clock, cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
    failed: { label: 'Échoué', icon: XCircle, cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

const StatusBadge = ({ status }) => {
    const cfg = STATUS[status] || { label: 'Inconnu', icon: AlertCircle, cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400' };
    const Icon = cfg.icon;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full ${cfg.cls}`}>
            <Icon className="w-3 h-3" />
            {cfg.label}
        </span>
    );
};

moment.locale("fr");

const BatchProductionList = ({ isDarkMode, createRequestKey = 0 }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [dateRange] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: "selection",
        },
    ]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchBatchProductions = async () => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            let url = '/batch-productions';
            const params = [];

            if (dateRange && dateRange.length > 0) {
                const range = dateRange[0];
                if (range.startDate) {
                    params.push(`startDate=${moment(range.startDate).format('YYYY-MM-DD')}`);
                }
                if (range.endDate) {
                    params.push(`endDate=${moment(range.endDate).format('YYYY-MM-DD')}`);
                }
            }

            if (debouncedSearchTerm) {
                params.push(`search=${encodeURIComponent(debouncedSearchTerm)}`);
            }

            if (params.length > 0) {
                url += `?${params.join('&')}`;
            }

            const response = await api.get(url, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.detail || "Erreur lors de la récupération des lots");
        }
    };

    const { data: batchesData, isLoading, error } = useQuery({
        queryKey: ['batch-productions', dateRange, debouncedSearchTerm],
        queryFn: fetchBatchProductions,
        keepPreviousData: true
    });

    useCreateRequest(createRequestKey, () => setShowModal(true));

    const batches = batchesData?.data || [];

    return (
        <div className="text-sm">
            <div className="flex flex-col sm:flex-row">
                <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Rechercher un lot..." />
            </div>

            {isLoading ? (
                <ListLoader label="Chargement des lots..." />
            ) : error ? (
                <ErrorLine message={error.message} />
            ) : (
                <ListCard>
                    {batches.length === 0 ? (
                        <ListEmpty
                            icon={Package}
                            title={debouncedSearchTerm ? 'Aucun lot trouvé' : 'Aucun lot de production'}
                            text={debouncedSearchTerm ? "Aucun lot ne correspond à votre recherche." : "Aucun lot de production disponible."}
                            action={!debouncedSearchTerm && (
                                <Button variant="tinted" size="sm" icon={Plus} className="mt-4" onClick={() => setShowModal(true)}>
                                    Nouveau lot
                                </Button>
                            )}
                        />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                    <th className={thClass}>Recette</th>
                                    <th className={`${thClass} text-right`}>Quantité</th>
                                    <th className={thClass}>Date</th>
                                    <th className={thClass}>Statut</th>
                                </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {batches.map((batch) => (
                                    <tr key={batch.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <td className={`${tdClass} font-semibold text-gray-900 dark:text-gray-100`}>{batch.recipe_name}</td>
                                        <td className={`${tdClass} text-right tabular-nums text-gray-700 dark:text-gray-300`}>{batch.quantity}</td>
                                        <td className={`${tdClass} text-gray-700 dark:text-gray-300`}>
                                            <span className="inline-flex items-center gap-1.5">
                                                <Calendar className="w-4 h-4 text-gray-400" />
                                                {moment(batch.production_date).format('DD/MM/YYYY')}
                                            </span>
                                        </td>
                                        <td className={tdClass}><StatusBadge status={batch.status} /></td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </ListCard>
            )}

            {showModal && (
                <BatchProductionModal
                    onClose={() => setShowModal(false)}
                    isDarkMode={isDarkMode}
                />
            )}
        </div>
    );
};

export default BatchProductionList;
